/**
 * notifications.admission.ts — who the backend admits to the DURABLE notification reader.
 *
 * MGI-PILOT-W2-DEFERRED-CLIENT-CAPABILITY-PERIMETER-CONVERGENCE-WS4 · H2 (client half).
 *
 * The backend contains `GET /api/notifications`, `GET /api/notifications/unread-count` and
 * `PATCH /api/notifications/:id/read` — the client reader of the durable K4B notification store, a
 * capability the signed P0 contract explicitly excludes ("no durable notification promise"; K4B
 * jobs/notifications are DEFERRED_NOT_CLOSED) — to exactly the roles below
 * (`DURABLE_NOTIFICATION_READER_ROLES` in `src/routes/notification.routes.js`). Every delivered pilot
 * identity (field_supervisor, route_operator, agency_employee, department_head) is refused at the role gate.
 *
 * THIS IS UX ADMISSION, NOT AUTHORISATION. It grants nothing, widens nothing and duplicates no decision:
 * it is the backend's own admission, mirrored where the client decides whether to ASK. Without it the
 * global bell would issue three requests that can only answer 403 for a delivered identity and then
 * present the refusal as "degradado / no se pudo sincronizar con backend" — a promise of a durable inbox
 * the contract never made. With it, a delivered identity's bell is what the contract sells it: the
 * realtime push (`notification:received`, a producer path this workstream does not touch), nothing more.
 * The backend remains the authority and refuses independently.
 *
 * Drift between this list and the backend constant is a cross-stack fact measured by the W2 parent
 * recomposition, which reads both repositories at their pinned anchors.
 */
export const DURABLE_NOTIFICATION_READER_ROLES = Object.freeze([
  'super_admin',
  'administration_manager',
  'group_manager',
] as const)

/** The identities the backend refuses on the durable reader — listed for the tests that pin the negative. */
export const DURABLE_NOTIFICATION_READER_REFUSED_ROLES = Object.freeze([
  'field_supervisor',
  'route_operator',
  'agency_employee',
  'department_head',
] as const)

export function isDurableNotificationReader(role: string | null | undefined): boolean {
  return (DURABLE_NOTIFICATION_READER_ROLES as readonly string[]).includes(String(role ?? ''))
}
